/** Reconoce errores del backend para armar un estado, sin traducirlos. `KNOWN_ERRORS` se queda en
 * `errors.ts` porque el check `known_errors` lo busca ahí por ruta fija; `errors.ts` re-exporta esto. */

/** Lo consulta la red de `lib/red-errores-mutacion.ts`: el manejo propio vive en `mutate(vars, { onError })`,
 * invisible para el manejador global (`#mutateOptions` es privado en query-core). */
const YA_COMUNICADOS = new WeakSet<object>()

/** Para todo `onError` que pinte un mensaje propio sin `translateApiError` ni las `is*`. Marca por
 * identidad, así que va sobre el mismo `err` (KAI-224). */
export function marcarErrorComoComunicado(err: unknown): void {
  if (typeof err === 'object' && err !== null) YA_COMUNICADOS.add(err)
}

export function errorYaComunicado(err: unknown): boolean {
  return typeof err === 'object' && err !== null && YA_COMUNICADOS.has(err)
}

function recordarSi(reconocido: boolean, err: unknown): boolean {
  if (reconocido) marcarErrorComoComunicado(err)
  return reconocido
}

export function isEvidenceRetentionConflict(err: unknown): boolean {
  const detail = (err as {response?: {data?: {detail?: string}}}).response?.data?.detail
  return recordarSi(detail === 'the employee has SG-SST evidence and must be deactivated instead', err)
}

export function isDesignationRetentionConflict(err: unknown): boolean {
  const detail = (err as {response?: {data?: {detail?: string}}}).response?.data?.detail
  return recordarSi(detail === 'the employee has SG-SST designations and must be deactivated instead', err)
}

export function isActionResponsibleRetentionConflict(err: unknown): boolean {
  const detail = (err as {response?: {data?: {detail?: string}}}).response?.data?.detail
  return recordarSi(
    detail === 'the employee is responsible for plan de mejoramiento actions and must be deactivated instead',
    err
  )
}

// La pantalla arma un estado (tarjeta bloqueada con enlace al perfil), no solo un aviso.
export function isBusinessProfileIncomplete(err: unknown): boolean {
  // `err` es null mientras la query carga; leer `.response` de null desmonta la app.
  if (err == null) return false
  const detail = (err as {response?: {data?: {detail?: string}}}).response?.data?.detail
  return recordarSi(detail === 'business profile incomplete', err)
}

// El 409 de un acto con compuerta de plan abre el modal de mejora (docs/modulos/upgrade-gating-spec.md §1).
const UPGRADE_GATE_DETAIL = 'this change crosses the paid segment band; an upgrade is required'

export function isUpgradeGateRequired(err: unknown): boolean {
  if (err == null) return false
  const detail = (err as {response?: {data?: {detail?: string}}}).response?.data?.detail
  return recordarSi(detail === UPGRADE_GATE_DETAIL, err)
}

// Sin `recordarSi` a propósito: el mensaje igual se muestra con `translateApiError`, y marcarlo antes
// apagaría la red si el llamador no llegara a pintarlo.
const NORMA_EN_CATALOGO = 'this norm is already in the Kairos catalog; incorporate it from Novedades'

export function isNormaEnCatalogo(err: unknown): boolean {
  if (err == null) return false
  return (err as {response?: {data?: {detail?: string}}}).response?.data?.detail === NORMA_EN_CATALOGO
}

// No es terminal: la pantalla pregunta si este concepto reemplaza al anterior.
const CONCEPTO_DUPLICADO_DETAIL = 'two concepts cannot share one radicado: say which one this replaces'

export function esConceptoDuplicado(err: unknown): boolean {
  return detalleDelError(err) === CONCEPTO_DUPLICADO_DETAIL
}

// Si la fecha es futura lo decide el backend (`bogota_today`), nunca el reloj del navegador.
const FECHA_FUTURA_DETAIL = 'a recorded exam date cannot be in the future without confirmation'

export function esFechaFuturaSinConfirmar(err: unknown): boolean {
  return detalleDelError(err) === FECHA_FUTURA_DETAIL
}

/** Se traducen por el `motivo` contra el mismo diccionario: así el check `known_errors` los cubre sin una
 * segunda lista. El resto de los campos interpola la frase. */
type DetalleConMotivo = {motivo: string} & Record<string, unknown>

export function detalleConMotivo(detail: unknown): DetalleConMotivo | null {
  if (typeof detail !== 'object' || detail === null || Array.isArray(detail)) return null
  const motivo = (detail as {motivo?: unknown}).motivo
  return typeof motivo === 'string' ? (detail as DetalleConMotivo) : null
}

export function motivoDelError(err: unknown): DetalleConMotivo | null {
  if (err == null) return null
  return detalleConMotivo((err as {response?: {data?: {detail?: unknown}}}).response?.data?.detail)
}

/** No marca el error como comunicado: el llamador abre `CompletarRosterDialog` con los `campos` que faltan. */
export function rosterIncompletoDetalle(err: unknown): {employeeId: string; campos: string[]} | null {
  const motivo = motivoDelError(err)
  if (motivo?.motivo !== 'roster_incompleto_bateria') return null
  const employeeId = motivo.employee_id
  const campos = motivo.campos
  if (typeof employeeId !== 'string' || !Array.isArray(campos)) return null
  return {employeeId, campos: campos.filter((c): c is string => typeof c === 'string')}
}

/** Para ramificar por un `detail` que no cabe en `KNOWN_ERRORS` porque lo comparten varias acciones; no
 * marca el error como comunicado. */
export function detalleDelError(err: unknown): string | null {
  if (err == null) return null
  const detail = (err as {response?: {data?: {detail?: unknown}}}).response?.data?.detail
  return typeof detail === 'string' ? detail : null
}
